import React, { useEffect, useState, useMemo } from "react";
import api, { employeesApi } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { salesReportGroup } from "../lib/serviceTypeNames";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  TrendingUp,
  DollarSign,
  Target,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Award,
  Layers,
  Globe,
  Sparkles,
  RefreshCw,
} from "lucide-react";
import { Badge } from "../components/ui/badge";

const SalesPnlPageNew = () => {
  const { user } = useAuth();
  const { displayCurrency, getCurrencySymbol, rates } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(2026);
  
  // Data - uses unified_projects instead of sales_projects_v2
  const [services, setServices] = useState([]);
  const [salesManagers, setSalesManagers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);

  // AI takeaways for the By Service Type card
  const [aiInsights, setAiInsights] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  
  // Exchange rates
  const usdToUah = rates?.USD || 42.97;
  const eurToUah = rates?.EUR || 45.5;

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [servicesRes, empRes, projectsRes, clientsRes] = await Promise.all([
        api.get(`/sales-services`),
        employeesApi.getAll(),
        api.get(`/unified-projects`),  // Use unified_projects!
        api.get(`/sales-clients`),
      ]);
      
      setServices(servicesRes.data || []);
      setProjects(projectsRes.data || []);
      setClients(clientsRes.data || []);
      
      const sales = (empRes.data || []).filter(e => e.position === "sales" && e.is_active);
      setSalesManagers(sales);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Convert to UAH
  const toUAH = (amount, currency) => {
    if (!amount) return 0;
    if (currency === "UAH") return amount;
    if (currency === "USD") return amount * usdToUah;
    if (currency === "EUR") return amount * eurToUah;
    return amount;
  };

  // Format with display currency
  const formatAmount = (amountUAH) => {
    let converted = amountUAH;
    if (displayCurrency === "USD") converted = amountUAH / usdToUah;
    else if (displayCurrency === "EUR") converted = amountUAH / eurToUah;
    
    const symbol = getCurrencySymbol();
    return `${symbol}${converted.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // Format short amount with k suffix (e.g., "150k")
  const formatShort = (amountUAH) => {
    if (!amountUAH || amountUAH === 0) return '—';
    let converted = amountUAH;
    if (displayCurrency === "USD") converted = amountUAH / usdToUah;
    else if (displayCurrency === "EUR") converted = amountUAH / eurToUah;
    return (converted / 1000).toFixed(0) + 'k';
  };

  // Month the money actually arrived, for the F (fact) buckets. paid_date is
  // stored in two formats (ISO "2026-06-01" and legacy "01.06.2026" from the
  // PrivatBank import). Falls back to the planned month when the date is
  // missing or lands outside the selected year (so year totals don't lose it).
  const factMonthOf = (s) => {
    const pd = s.paid_date || s.payment_date;
    let pm = null;
    if (pd) {
      if (/^\d{4}-\d{2}/.test(pd)) {
        pm = String(pd).slice(0, 7);
      } else {
        const m = String(pd).match(/^(\d{2})\.(\d{2})\.(\d{4})/);
        if (m) pm = `${m[3]}-${m[2]}`;
      }
    }
    return pm && pm.startsWith(String(selectedYear)) ? pm : s.month;
  };

  // Get manager ID for a service (uses unified_projects + client fallback)
  const getServiceManager = (service) => {
    const project = projects.find(p => p.id === service.project_id);
    if (!project) return null;
    
    if (project.sales_manager_id) {
      return project.sales_manager_id;
    }
    
    // Fallback to client's sales_manager_id
    const client = clients.find(c => c.id === project.client_id);
    return client?.sales_manager_id || null;
  };

  // Partnership manager IDs
  const partnershipManagerIds = useMemo(() => {
    return new Set(salesManagers.filter(m => m.is_partnership).map(m => m.id));
  }, [salesManagers]);

  // Filter services for current year
  const yearServices = useMemo(() => {
    return services.filter(s => s.month?.startsWith(String(selectedYear)));
  }, [services, selectedYear]);

  // Visible managers based on role
  const visibleManagers = useMemo(() => {
    if (user?.role === "admin") {
      return salesManagers;
    }
    // For commercial director, exclude partnership managers
    return salesManagers.filter(m => !m.is_partnership);
  }, [salesManagers, user?.role]);

  // Calculate stats by manager
  const managerStats = useMemo(() => {
    const stats = {};
    const managerIds = new Set(visibleManagers.map(m => m.id));
    
    // Initialize stats for each manager
    visibleManagers.forEach(m => {
      stats[m.id] = {
        id: m.id,
        name: m.name,
        is_partnership: m.is_partnership,
        months: {}
      };
      // Initialize months
      for (let i = 1; i <= 12; i++) {
        const monthKey = `${selectedYear}-${String(i).padStart(2, '0')}`;
        stats[m.id].months[monthKey] = { plan: 0, fact: 0, all: 0 };
      }
    });
    
    // Add unassigned bucket
    stats['unassigned'] = {
      id: 'unassigned',
      name: 'Unassigned',
      is_unassigned: true,
      months: {}
    };
    for (let i = 1; i <= 12; i++) {
      const monthKey = `${selectedYear}-${String(i).padStart(2, '0')}`;
      stats['unassigned'].months[monthKey] = { plan: 0, fact: 0, all: 0 };
    }
    
    // Process services
    yearServices.forEach(s => {
      const managerId = getServiceManager(s);
      const month = s.month;
      const amountUAH = toUAH(s.amount || 0, s.currency);
      
      // For non-admin roles, completely exclude services from partnership managers
      if (user?.role !== "admin" && managerId && partnershipManagerIds.has(managerId)) {
        return; // Skip this service entirely
      }
      
      // Determine target bucket
      let targetId = managerId;
      if (!managerId || !managerIds.has(managerId)) {
        targetId = 'unassigned';
      }
      
      if (!stats[targetId]?.months[month]) return;

      stats[targetId].months[month].all += amountUAH;

      if ((s.probability || 0) >= 50) {
        stats[targetId].months[month].plan += amountUAH;
      }
      if (s.is_paid) {
        // Fact lands in the month the money actually arrived, not the planned one
        const fm = factMonthOf(s);
        if (stats[targetId].months[fm]) stats[targetId].months[fm].fact += amountUAH;
      }
    });
    
    return stats;
  }, [visibleManagers, yearServices, selectedYear, projects, clients, user?.role, partnershipManagerIds]);

  // Calculate totals by month
  const monthlyTotals = useMemo(() => {
    const totals = {};
    for (let i = 1; i <= 12; i++) {
      const monthKey = `${selectedYear}-${String(i).padStart(2, '0')}`;
      totals[monthKey] = { plan: 0, fact: 0, all: 0 };
    }
    
    Object.values(managerStats).forEach(manager => {
      if (manager.is_unassigned) return; // Don't include unassigned in totals
      Object.entries(manager.months).forEach(([month, data]) => {
        totals[month].plan += data.plan;
        totals[month].fact += data.fact;
        totals[month].all += data.all;
      });
    });
    
    return totals;
  }, [managerStats, selectedYear]);

  // Chart data
  const chartData = useMemo(() => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months.map((name, i) => {
      const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
      const data = monthlyTotals[monthKey] || { plan: 0, fact: 0 };
      return {
        name,
        'Plan (≥50%)': Math.round(data.plan / (displayCurrency === 'UAH' ? 1 : usdToUah)),
        'Fact (Paid)': Math.round(data.fact / (displayCurrency === 'UAH' ? 1 : usdToUah)),
      };
    });
  }, [monthlyTotals, selectedYear, displayCurrency, usdToUah]);

  // Year totals
  const yearTotals = useMemo(() => {
    let plan = 0, fact = 0;
    Object.values(monthlyTotals).forEach(m => {
      plan += m.plan;
      fact += m.fact;
    });
    return { plan, fact };
  }, [monthlyTotals]);

  // H1/H2 totals
  const halfYearTotals = useMemo(() => {
    let h1Plan = 0, h1Fact = 0, h2Plan = 0, h2Fact = 0;
    Object.entries(monthlyTotals).forEach(([month, data]) => {
      const m = parseInt(month.split('-')[1]);
      if (m <= 6) {
        h1Plan += data.plan;
        h1Fact += data.fact;
      } else {
        h2Plan += data.plan;
        h2Fact += data.fact;
      }
    });
    return { h1: { plan: h1Plan, fact: h1Fact }, h2: { plan: h2Plan, fact: h2Fact } };
  }, [monthlyTotals]);

  // Service statistics - group by service type WITH monthly breakdown
  const serviceStats = useMemo(() => {
    const stats = {};
    
    yearServices.forEach(s => {
      const managerId = getServiceManager(s);
      
      // For non-admin roles, skip partnership manager services
      if (user?.role !== "admin" && managerId && partnershipManagerIds.has(managerId)) {
        return;
      }
      
      const serviceType = salesReportGroup(s.service_type) || salesReportGroup(s.name) || 'Other';
      const amountUAH = toUAH(s.amount || 0, s.currency);
      const month = s.month;
      
      if (!stats[serviceType]) {
        stats[serviceType] = { 
          name: serviceType, 
          plan: 0, 
          fact: 0, 
          all: 0, 
          count: 0,
          months: {}
        };
        // Initialize months
        for (let i = 1; i <= 12; i++) {
          const monthKey = `${selectedYear}-${String(i).padStart(2, '0')}`;
          stats[serviceType].months[monthKey] = { plan: 0, fact: 0 };
        }
      }
      
      stats[serviceType].all += amountUAH;
      stats[serviceType].count += 1;
      
      if ((s.probability || 0) >= 50) {
        stats[serviceType].plan += amountUAH;
        if (stats[serviceType].months[month]) {
          stats[serviceType].months[month].plan += amountUAH;
        }
      }
      if (s.is_paid) {
        stats[serviceType].fact += amountUAH;
        const fm = factMonthOf(s);
        if (stats[serviceType].months[fm]) {
          stats[serviceType].months[fm].fact += amountUAH;
        }
      }
    });
    
    return Object.values(stats).sort((a, b) => b.fact - a.fact);
  }, [yearServices, user?.role, partnershipManagerIds, projects, clients, selectedYear]);

  // Fetch AI takeaways for the service mix. Server caches per (year, currency,
  // role) for 24h, so the auto-call on mount is usually a cache hit.
  const fetchAiInsights = async (regenerate = false) => {
    if (!serviceStats.length) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const fromUAH = (uah) => {
        if (displayCurrency === "USD") return uah / usdToUah;
        if (displayCurrency === "EUR") return uah / eurToUah;
        return uah;
      };
      const monthKeys = Array.from({ length: 12 }, (_, i) => `${selectedYear}-${String(i + 1).padStart(2, "0")}`);
      const rows = serviceStats.map((s) => ({
        name: s.name,
        fact: Math.round(fromUAH(s.fact)),
        count: s.count,
        monthly: monthKeys.map((k) => Math.round(fromUAH(s.months[k]?.fact || 0))),
      }));
      const res = await api.post(`/sales-pnl/ai-insights`, {
        year: selectedYear,
        currency: displayCurrency,
        regenerate,
        rows,
        total_fact: rows.reduce((sum, r) => sum + r.fact, 0),
      });
      setAiInsights(res.data);
    } catch (error) {
      setAiError(error.response?.data?.detail || "Failed to generate insights");
    } finally {
      setAiLoading(false);
    }
  };

  // Country statistics - group by project country WITH monthly breakdown
  const countryStats = useMemo(() => {
    const stats = {};
    
    yearServices.forEach(s => {
      const managerId = getServiceManager(s);
      
      // For non-admin roles, skip partnership manager services
      if (user?.role !== "admin" && managerId && partnershipManagerIds.has(managerId)) {
        return;
      }
      
      // Get project country, falling back to the client's country — half the
      // legacy projects have no country set (the field is optional on create
      // and absent from the edit form), but their clients usually do.
      const project = projects.find(p => p.id === s.project_id);
      const projectClient = project ? clients.find(c => c.id === project.client_id) : null;
      const country = project?.country || projectClient?.country || 'Unknown';
      const amountUAH = toUAH(s.amount || 0, s.currency);
      const month = s.month;
      
      if (!stats[country]) {
        stats[country] = { 
          name: country, 
          plan: 0, 
          fact: 0, 
          count: 0,
          months: {}
        };
        // Initialize months
        for (let i = 1; i <= 12; i++) {
          const monthKey = `${selectedYear}-${String(i).padStart(2, '0')}`;
          stats[country].months[monthKey] = { plan: 0, fact: 0 };
        }
      }
      
      stats[country].count += 1;
      
      if ((s.probability || 0) >= 50) {
        stats[country].plan += amountUAH;
        if (stats[country].months[month]) {
          stats[country].months[month].plan += amountUAH;
        }
      }
      if (s.is_paid) {
        stats[country].fact += amountUAH;
        const fm = factMonthOf(s);
        if (stats[country].months[fm]) {
          stats[country].months[fm].fact += amountUAH;
        }
      }
    });
    
    return Object.values(stats).sort((a, b) => b.fact - a.fact);
  }, [yearServices, user?.role, partnershipManagerIds, projects, clients, selectedYear]);

  // Auto-load AI takeaways once data is in (server cache makes this cheap);
  // re-fetch when year or display currency changes.
  useEffect(() => {
    if (!loading && serviceStats.length > 0) {
      fetchAiInsights(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, selectedYear, displayCurrency]);

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  // Index of the in-progress month (partial data) when viewing the current year
  const currentMonthIdx = new Date().getFullYear() === selectedYear ? new Date().getMonth() : -1;

  const totalServiceFact = serviceStats.reduce((sum, s) => sum + s.fact, 0);
  const totalCountryFact = countryStats.reduce((sum, c) => sum + c.fact, 0);

  // Minimal markdown for the AI text: bullet lines with **bold** fragments
  const renderInsightText = (text) =>
    text.split("\n").map(l => l.trim()).filter(Boolean).map((line, i) => {
      const parts = line.replace(/^[-*•]\s*/, "").split(/\*\*(.+?)\*\*/g);
      return (
        <li key={i}>
          {parts.map((p, j) => (j % 2 ? <strong key={j} className="text-zinc-50 font-semibold">{p}</strong> : p))}
        </li>
      );
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  // Sort managers: CD first, then by year total
  const sortedManagers = Object.values(managerStats)
    .filter(m => !m.is_unassigned)
    .sort((a, b) => {
      // Commercial Director (Kuzmenko) first
      if (a.name?.toLowerCase().includes('kuzmenko')) return -1;
      if (b.name?.toLowerCase().includes('kuzmenko')) return 1;
      // Then by total
      const aTotal = Object.values(a.months).reduce((sum, m) => sum + m.plan, 0);
      const bTotal = Object.values(b.months).reduce((sum, m) => sum + m.plan, 0);
      return bTotal - aTotal;
    });

  // Add unassigned at the end if it has data
  const unassigned = managerStats['unassigned'];
  const unassignedTotal = Object.values(unassigned?.months || {}).reduce((sum, m) => sum + m.plan, 0);

  return (
    <div className="p-6 space-y-6" data-testid="sales-pnl-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-green-500" />
            Sales P&L
          </h1>
          <p className="text-zinc-400 mt-1">Plan and Fact by Sales Manager</p>
        </div>
        
        {/* Year Selector */}
        <div className="flex items-center gap-2 bg-zinc-800 rounded-lg p-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedYear(y => y - 1)}
            className="text-zinc-400 hover:text-zinc-50"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-zinc-50 font-medium px-4">{selectedYear}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedYear(y => y + 1)}
            className="text-zinc-400 hover:text-zinc-50"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Target className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-zinc-400">Year Plan (≥50%)</p>
                <p className="text-xl font-bold text-blue-400">{formatAmount(yearTotals.plan)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-xs text-zinc-400">Year Fact (Paid)</p>
                <p className="text-xl font-bold text-green-400">{formatAmount(yearTotals.fact)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <DollarSign className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-zinc-400">H1 Plan</p>
                <p className="text-xl font-bold text-purple-400">{formatAmount(halfYearTotals.h1.plan)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <Award className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <p className="text-xs text-zinc-400">Conversion Rate</p>
                <p className="text-xl font-bold text-orange-400">
                  {yearTotals.plan > 0 ? Math.round((yearTotals.fact / yearTotals.plan) * 100) : 0}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-50 text-lg">Monthly Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="name" stroke="#888" />
                <YAxis stroke="#888" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1f1f1f', border: '1px solid #333' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Legend />
                <Bar dataKey="Plan (≥50%)" fill="#3b82f6" />
                <Bar dataKey="Fact (Paid)" fill="#22c55e" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Manager Table */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-50 text-lg flex items-center gap-2">
            <Target className="h-5 w-5 text-blue-500" />
            Plan by Manager - {selectedYear}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-700">
                  <TableHead className="text-zinc-400 sticky left-0 bg-zinc-900">Manager</TableHead>
                  {monthNames.map(m => (
                    <TableHead key={m} className="text-zinc-400 text-center">{m}</TableHead>
                  ))}
                  <TableHead className="text-zinc-400 text-center bg-zinc-800">H1</TableHead>
                  <TableHead className="text-zinc-400 text-center bg-zinc-800">H2</TableHead>
                  <TableHead className="text-zinc-400 text-center bg-zinc-800 font-bold">Year</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedManagers.map(manager => {
                  const h1 = Object.entries(manager.months)
                    .filter(([m]) => parseInt(m.split('-')[1]) <= 6)
                    .reduce((sum, [, d]) => sum + d.plan, 0);
                  const h2 = Object.entries(manager.months)
                    .filter(([m]) => parseInt(m.split('-')[1]) > 6)
                    .reduce((sum, [, d]) => sum + d.plan, 0);
                  const total = h1 + h2;
                  
                  return (
                    <TableRow key={manager.id} className="border-zinc-700 hover:bg-zinc-800/50">
                      <TableCell className="sticky left-0 bg-zinc-900 font-medium text-zinc-50">
                        <div className="flex items-center gap-2">
                          {manager.name}
                          {manager.name?.toLowerCase().includes('kuzmenko') && (
                            <Badge className="bg-orange-500/20 text-orange-400 text-xs">CD</Badge>
                          )}
                          {manager.is_partnership && (
                            <Badge className="bg-purple-500/20 text-purple-400 text-xs">P</Badge>
                          )}
                        </div>
                      </TableCell>
                      {monthNames.map((_, i) => {
                        const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
                        const value = manager.months[monthKey]?.plan || 0;
                        return (
                          <TableCell key={monthKey} className="text-center font-mono">
                            <span className={cn(
                              value > 0 ? "text-blue-400" : "text-zinc-600"
                            )}>
                              {value > 0 ? formatAmount(value) : "—"}
                            </span>
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center font-mono bg-zinc-800/50 text-purple-400">
                        {h1 > 0 ? formatAmount(h1) : "—"}
                      </TableCell>
                      <TableCell className="text-center font-mono bg-zinc-800/50 text-purple-400">
                        {h2 > 0 ? formatAmount(h2) : "—"}
                      </TableCell>
                      <TableCell className="text-center font-mono font-bold bg-zinc-800/50 text-green-400">
                        {total > 0 ? formatAmount(total) : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
                
                {/* Unassigned row if has data */}
                {unassignedTotal > 0 && (
                  <TableRow className="border-zinc-700 bg-yellow-500/5">
                    <TableCell className="sticky left-0 bg-zinc-900 font-medium text-yellow-500">
                      Unassigned
                    </TableCell>
                    {monthNames.map((_, i) => {
                      const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
                      const value = unassigned.months[monthKey]?.plan || 0;
                      return (
                        <TableCell key={monthKey} className="text-center font-mono text-yellow-500">
                          {value > 0 ? formatAmount(value) : "—"}
                        </TableCell>
                      );
                    })}
                    <TableCell className="text-center font-mono bg-zinc-800/50 text-yellow-500">
                      {formatAmount(Object.entries(unassigned.months).filter(([m]) => parseInt(m.split('-')[1]) <= 6).reduce((sum, [, d]) => sum + d.plan, 0))}
                    </TableCell>
                    <TableCell className="text-center font-mono bg-zinc-800/50 text-yellow-500">
                      {formatAmount(Object.entries(unassigned.months).filter(([m]) => parseInt(m.split('-')[1]) > 6).reduce((sum, [, d]) => sum + d.plan, 0))}
                    </TableCell>
                    <TableCell className="text-center font-mono font-bold bg-zinc-800/50 text-yellow-500">
                      {formatAmount(unassignedTotal)}
                    </TableCell>
                  </TableRow>
                )}
                
                {/* Totals row */}
                <TableRow className="border-zinc-700 bg-zinc-800 font-bold">
                  <TableCell className="sticky left-0 bg-zinc-800 text-zinc-50">TOTAL</TableCell>
                  {monthNames.map((_, i) => {
                    const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
                    const value = monthlyTotals[monthKey]?.plan || 0;
                    return (
                      <TableCell key={monthKey} className="text-center font-mono text-blue-400">
                        {value > 0 ? formatAmount(value) : "—"}
                      </TableCell>
                    );
                  })}
                  <TableCell className="text-center font-mono bg-zinc-700 text-purple-400">
                    {formatAmount(halfYearTotals.h1.plan)}
                  </TableCell>
                  <TableCell className="text-center font-mono bg-zinc-700 text-purple-400">
                    {formatAmount(halfYearTotals.h2.plan)}
                  </TableCell>
                  <TableCell className="text-center font-mono font-bold bg-zinc-700 text-green-400">
                    {formatAmount(yearTotals.plan)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Fact Table */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-50 text-lg flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-500" />
            Fact (Paid) by Manager - {selectedYear}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-700">
                  <TableHead className="text-zinc-400 sticky left-0 bg-zinc-900">Manager</TableHead>
                  {monthNames.map(m => (
                    <TableHead key={m} className="text-zinc-400 text-center">{m}</TableHead>
                  ))}
                  <TableHead className="text-zinc-400 text-center bg-zinc-800">H1</TableHead>
                  <TableHead className="text-zinc-400 text-center bg-zinc-800">H2</TableHead>
                  <TableHead className="text-zinc-400 text-center bg-zinc-800 font-bold">Year</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedManagers.map(manager => {
                  const h1 = Object.entries(manager.months)
                    .filter(([m]) => parseInt(m.split('-')[1]) <= 6)
                    .reduce((sum, [, d]) => sum + d.fact, 0);
                  const h2 = Object.entries(manager.months)
                    .filter(([m]) => parseInt(m.split('-')[1]) > 6)
                    .reduce((sum, [, d]) => sum + d.fact, 0);
                  const total = h1 + h2;
                  
                  return (
                    <TableRow key={manager.id} className="border-zinc-700 hover:bg-zinc-800/50">
                      <TableCell className="sticky left-0 bg-zinc-900 font-medium text-zinc-50">
                        <div className="flex items-center gap-2">
                          {manager.name}
                          {manager.name?.toLowerCase().includes('kuzmenko') && (
                            <Badge className="bg-orange-500/20 text-orange-400 text-xs">CD</Badge>
                          )}
                          {manager.is_partnership && (
                            <Badge className="bg-purple-500/20 text-purple-400 text-xs">P</Badge>
                          )}
                        </div>
                      </TableCell>
                      {monthNames.map((_, i) => {
                        const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
                        const value = manager.months[monthKey]?.fact || 0;
                        return (
                          <TableCell key={monthKey} className="text-center font-mono">
                            <span className={cn(
                              value > 0 ? "text-green-400" : "text-zinc-600"
                            )}>
                              {value > 0 ? formatAmount(value) : "—"}
                            </span>
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center font-mono bg-zinc-800/50 text-purple-400">
                        {h1 > 0 ? formatAmount(h1) : "—"}
                      </TableCell>
                      <TableCell className="text-center font-mono bg-zinc-800/50 text-purple-400">
                        {h2 > 0 ? formatAmount(h2) : "—"}
                      </TableCell>
                      <TableCell className="text-center font-mono font-bold bg-zinc-800/50 text-green-400">
                        {total > 0 ? formatAmount(total) : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
                
                {/* Totals row */}
                <TableRow className="border-zinc-700 bg-zinc-800 font-bold">
                  <TableCell className="sticky left-0 bg-zinc-800 text-zinc-50">TOTAL</TableCell>
                  {monthNames.map((_, i) => {
                    const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
                    const value = monthlyTotals[monthKey]?.fact || 0;
                    return (
                      <TableCell key={monthKey} className="text-center font-mono text-green-400">
                        {value > 0 ? formatAmount(value) : "—"}
                      </TableCell>
                    );
                  })}
                  <TableCell className="text-center font-mono bg-zinc-700 text-purple-400">
                    {formatAmount(halfYearTotals.h1.fact)}
                  </TableCell>
                  <TableCell className="text-center font-mono bg-zinc-700 text-purple-400">
                    {formatAmount(halfYearTotals.h2.fact)}
                  </TableCell>
                  <TableCell className="text-center font-mono font-bold bg-zinc-700 text-green-400">
                    {formatAmount(yearTotals.fact)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Combined Plan/Fact Table with Monthly Breakdown */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-50 text-lg flex items-center gap-2">
            <Layers className="h-5 w-5 text-purple-500" />
            Plan vs Fact by Manager - {selectedYear}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-700">
                  <TableHead className="text-zinc-400 sticky left-0 bg-zinc-900 z-10">Manager</TableHead>
                  {monthNames.map((m, i) => (
                    <TableHead key={i} className="text-zinc-400 text-center text-xs min-w-[70px]" colSpan={2}>
                      {m}
                    </TableHead>
                  ))}
                  <TableHead className="text-zinc-400 text-center bg-zinc-800">Year Total</TableHead>
                  <TableHead className="text-zinc-400 text-center">Conv</TableHead>
                </TableRow>
                <TableRow className="border-zinc-700 text-[10px]">
                  <TableHead className="text-zinc-500 sticky left-0 bg-zinc-900 z-10"></TableHead>
                  {monthNames.map((_, i) => (
                    <React.Fragment key={i}>
                      <TableHead className="text-blue-400 text-center px-1 min-w-[35px]">P</TableHead>
                      <TableHead className="text-green-400 text-center px-1 min-w-[35px]">F</TableHead>
                    </React.Fragment>
                  ))}
                  <TableHead className="text-zinc-400 text-center bg-zinc-800"></TableHead>
                  <TableHead className="text-zinc-500 text-center"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedManagers.map(manager => {
                  const plan = Object.values(manager.months).reduce((sum, m) => sum + m.plan, 0);
                  const fact = Object.values(manager.months).reduce((sum, m) => sum + m.fact, 0);
                  const conversion = plan > 0 ? Math.round((fact / plan) * 100) : 0;
                  
                  return (
                    <TableRow key={manager.id} className="border-zinc-700 hover:bg-zinc-800/50">
                      <TableCell className="sticky left-0 bg-zinc-900 font-medium text-zinc-50 z-10">
                        <div className="flex items-center gap-1">
                          <span className="truncate max-w-[100px]">{manager.name}</span>
                          {manager.name?.toLowerCase().includes('kuzmenko') && (
                            <Badge className="bg-orange-500/20 text-orange-400 text-[8px] px-1">CD</Badge>
                          )}
                        </div>
                      </TableCell>
                      {monthNames.map((_, i) => {
                        const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
                        const monthData = manager.months[monthKey] || { plan: 0, fact: 0 };
                        return (
                          <React.Fragment key={monthKey}>
                            <TableCell className="text-center font-mono text-blue-400 text-xs px-1">
                              {monthData.plan > 0 ? formatShort(monthData.plan) : '—'}
                            </TableCell>
                            <TableCell className="text-center font-mono text-green-400 text-xs px-1">
                              {monthData.fact > 0 ? formatShort(monthData.fact) : '—'}
                            </TableCell>
                          </React.Fragment>
                        );
                      })}
                      <TableCell className="text-center font-mono text-purple-400 bg-zinc-800">
                        {formatAmount(plan)}
                      </TableCell>
                      <TableCell className="text-center font-mono">
                        <span className={cn(
                          conversion >= 80 ? "text-green-400" :
                          conversion >= 50 ? "text-yellow-400" :
                          conversion > 0 ? "text-orange-400" : "text-zinc-600"
                        )}>
                          {conversion > 0 ? `${conversion}%` : '—'}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
                
                {/* Totals */}
                <TableRow className="border-zinc-700 bg-zinc-800 font-bold">
                  <TableCell className="sticky left-0 bg-zinc-800 text-zinc-50 z-10">TOTAL</TableCell>
                  {monthNames.map((_, i) => {
                    const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
                    return (
                      <React.Fragment key={monthKey}>
                        <TableCell className="text-center font-mono text-blue-400 text-xs">
                          {(monthlyTotals[monthKey]?.plan || 0) > 0 ? formatShort(monthlyTotals[monthKey]?.plan || 0) : '—'}
                        </TableCell>
                        <TableCell className="text-center font-mono text-green-400 text-xs">
                          {(monthlyTotals[monthKey]?.fact || 0) > 0 ? formatShort(monthlyTotals[monthKey]?.fact || 0) : '—'}
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell className="text-center font-mono text-purple-400">
                    {formatAmount(yearTotals.plan)}
                  </TableCell>
                  <TableCell className="text-center font-mono text-yellow-400">
                    {yearTotals.plan > 0 ? `${Math.round((yearTotals.fact / yearTotals.plan) * 100)}%` : '—'}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Service mix — AI takeaways + fact-only monthly breakdown */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-50 text-lg flex items-center justify-between">
            <span className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-cyan-500" />
              By Service Type - {selectedYear}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="text-zinc-400 hover:text-zinc-50"
              onClick={() => fetchAiInsights(true)}
              disabled={aiLoading}
            >
              <RefreshCw className={cn("h-4 w-4 mr-1", aiLoading && "animate-spin")} />
              Refresh AI
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* AI takeaways */}
          <div className="mb-4 rounded-lg border border-zinc-800 bg-zinc-950/60 p-4">
            <div className="flex items-center gap-2 mb-2 text-sm font-medium text-cyan-400">
              <Sparkles className="h-4 w-4" />
              AI Takeaways
              {aiInsights?.generated_at && (
                <span className="text-[10px] text-zinc-600 font-normal">
                  {new Date(aiInsights.generated_at).toLocaleString()}{aiInsights.cached ? " · cached" : ""}
                </span>
              )}
            </div>
            {aiLoading && !aiInsights ? (
              <div className="flex items-center gap-2 text-zinc-500 text-sm py-2">
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-cyan-500" />
                Analyzing sales mix...
              </div>
            ) : aiError ? (
              <div className="text-red-400 text-sm">{aiError}</div>
            ) : aiInsights?.text ? (
              <ul className="space-y-1.5 text-sm text-zinc-300 list-disc list-inside marker:text-cyan-600">
                {renderInsightText(aiInsights.text)}
              </ul>
            ) : (
              <div className="text-zinc-600 text-sm">No insights yet</div>
            )}
          </div>

          {/* Fact-only table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-700">
                  <TableHead className="text-zinc-400 sticky left-0 bg-zinc-900 z-10">Service Type</TableHead>
                  {monthNames.map((m, i) => (
                    <TableHead
                      key={i}
                      className={cn("text-zinc-400 text-center text-xs min-w-[48px]", i === currentMonthIdx && "text-cyan-400")}
                      title={i === currentMonthIdx ? "Current month — partial data" : undefined}
                    >
                      {m}{i === currentMonthIdx ? "*" : ""}
                    </TableHead>
                  ))}
                  <TableHead className="text-zinc-400 text-center bg-zinc-800">Year Total</TableHead>
                  <TableHead className="text-zinc-400 text-center">Share</TableHead>
                  <TableHead className="text-zinc-400 text-center">Sales</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {serviceStats.map((service, idx) => {
                  const share = totalServiceFact > 0 && service.fact > 0
                    ? Math.round((service.fact / totalServiceFact) * 100) : 0;
                  return (
                    <TableRow key={idx} className={cn(
                      "border-zinc-700 hover:bg-zinc-800/50",
                      service.fact === 0 && "opacity-50"
                    )}>
                      <TableCell className="sticky left-0 bg-zinc-900 font-medium text-zinc-50 z-10">
                        <span className="truncate max-w-[160px] block">{service.name}</span>
                      </TableCell>
                      {monthNames.map((_, i) => {
                        const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
                        const fact = service.months[monthKey]?.fact || 0;
                        return (
                          <TableCell key={monthKey} className="text-center font-mono text-green-400 text-xs px-1">
                            {fact > 0 ? formatShort(fact) : '—'}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center font-mono text-green-400 bg-zinc-800">
                        {service.fact > 0 ? formatAmount(service.fact) : '—'}
                      </TableCell>
                      <TableCell className="text-center font-mono text-zinc-300">
                        {share > 0 ? `${share}%` : '—'}
                      </TableCell>
                      <TableCell className="text-center font-mono text-zinc-500">
                        {service.count}
                      </TableCell>
                    </TableRow>
                  );
                })}

                {/* Service Totals */}
                <TableRow className="border-zinc-700 bg-zinc-800 font-bold">
                  <TableCell className="sticky left-0 bg-zinc-800 text-zinc-50 z-10">TOTAL</TableCell>
                  {monthNames.map((_, i) => {
                    const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
                    const factTotal = serviceStats.reduce((sum, s) => sum + (s.months[monthKey]?.fact || 0), 0);
                    return (
                      <TableCell key={monthKey} className="text-center font-mono text-green-400 text-xs">
                        {factTotal > 0 ? formatShort(factTotal) : '—'}
                      </TableCell>
                    );
                  })}
                  <TableCell className="text-center font-mono text-green-400">
                    {formatAmount(totalServiceFact)}
                  </TableCell>
                  <TableCell className="text-center font-mono text-zinc-400">100%</TableCell>
                  <TableCell className="text-center font-mono text-zinc-400">
                    {serviceStats.reduce((sum, s) => sum + s.count, 0)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* By Country — fact-only monthly breakdown */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-50 text-lg flex items-center gap-2">
            <Globe className="h-5 w-5 text-emerald-500" />
            By Country - {selectedYear}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-700">
                  <TableHead className="text-zinc-400 sticky left-0 bg-zinc-900 z-10">Country</TableHead>
                  {monthNames.map((m, i) => (
                    <TableHead
                      key={i}
                      className={cn("text-zinc-400 text-center text-xs min-w-[48px]", i === currentMonthIdx && "text-cyan-400")}
                      title={i === currentMonthIdx ? "Current month — partial data" : undefined}
                    >
                      {m}{i === currentMonthIdx ? "*" : ""}
                    </TableHead>
                  ))}
                  <TableHead className="text-zinc-400 text-center bg-zinc-800">Year Total</TableHead>
                  <TableHead className="text-zinc-400 text-center">Share</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {countryStats.map((country, idx) => {
                  const share = totalCountryFact > 0 && country.fact > 0
                    ? Math.round((country.fact / totalCountryFact) * 100) : 0;
                  return (
                    <TableRow key={idx} className={cn(
                      "border-zinc-700 hover:bg-zinc-800/50",
                      country.fact === 0 && "opacity-50"
                    )}>
                      <TableCell className="sticky left-0 bg-zinc-900 font-medium text-zinc-50 z-10">
                        <span className="truncate max-w-[120px] block">{country.name}</span>
                      </TableCell>
                      {monthNames.map((_, i) => {
                        const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
                        const fact = country.months[monthKey]?.fact || 0;
                        return (
                          <TableCell key={monthKey} className="text-center font-mono text-green-400 text-xs px-1">
                            {fact > 0 ? formatShort(fact) : '—'}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center font-mono text-green-400 bg-zinc-800">
                        {country.fact > 0 ? formatAmount(country.fact) : '—'}
                      </TableCell>
                      <TableCell className="text-center font-mono text-zinc-300">
                        {share > 0 ? `${share}%` : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}

                {/* Country Totals */}
                <TableRow className="border-zinc-700 bg-zinc-800 font-bold">
                  <TableCell className="sticky left-0 bg-zinc-800 text-zinc-50 z-10">TOTAL</TableCell>
                  {monthNames.map((_, i) => {
                    const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
                    const factTotal = countryStats.reduce((sum, c) => sum + (c.months[monthKey]?.fact || 0), 0);
                    return (
                      <TableCell key={monthKey} className="text-center font-mono text-green-400 text-xs">
                        {factTotal > 0 ? formatShort(factTotal) : '—'}
                      </TableCell>
                    );
                  })}
                  <TableCell className="text-center font-mono text-green-400">
                    {formatAmount(totalCountryFact)}
                  </TableCell>
                  <TableCell className="text-center font-mono text-zinc-400">100%</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SalesPnlPageNew;
